import { AppContext } from "@components/features/appContext/AppContext";
import Layout from "@components/primitives/layout/Layout";
import { createTitle } from "@utils/config";
import { translation } from "@utils/translation";
import { Tooltip } from "react-tooltip";
import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import useFetchEvents from "@hooks/events/useFetchEvents";
import { EventType } from "next-lib";
import moment from "moment";
import "moment/locale/nl";
import "moment/locale/en-gb";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import esLocale from "@fullcalendar/core/locales/es";
import nlLocale from "@fullcalendar/core/locales/nl";
import { EventContentArg } from "@fullcalendar/core";
import AlertBox from "@components/primitives/alertBox/AlertBox";
import classNames from "classnames";

function EventsPage() {
  const { language } = useContext(AppContext);
  const { events } = useFetchEvents(language);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  moment.locale(language.split("_")[0]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatEvents = events?.map((event: EventType) => {
    return {
      id: event._id,
      title: event.title,
      description: event.location,
      start: moment(event.startDate).format("YYYY-MM-DD HH:mm"),
      end: moment(event.endDate).format("YYYY-MM-DD HH:mm"),
    };
  });

  const renderEventContent = (eventInfo: EventContentArg) => {
    return (
      <div>
        <div
          data-tooltip-id={`tooltip-${eventInfo.event.id}`}
          data-tooltip-html={`${translation(language).events.location}:<br/>
          ${eventInfo.event.extendedProps.description}
          </br>
          ${translation(language).events.time}:
          ${moment(eventInfo.event.start).format("HH:mm")} - ${moment(
            eventInfo.event.end
          ).format("HH:mm")}`}
          data-tooltip-place="top"
        >
          {eventInfo.event.title}
        </div>
        <Tooltip id={`tooltip-${eventInfo.event.id}`} place="top" />
      </div>
    );
  };

  return (
    <Layout>
      <Helmet>
        <title>{createTitle(translation(language).header.events)}</title>
      </Helmet>
      <div className="h-max bg-primary w-full pt-32 pb-20 px-5">
        <h1 className="md:text-3xl text-2xl mb-2">
          {translation(language).events.title}
        </h1>
        <p className="text-secondary mb-2">
          {translation(language).events.description}
        </p>
        <div className="w-full h-full flex items-center justify-center flex-col gap-5">
          <div className="md:px-16 md:pt-10 pt-5 md:w-3/4 w-full h-full">
            <FullCalendar
              plugins={[dayGridPlugin]}
              viewClassNames="bg-secondary"
              eventClassNames="bg-[#4ab6c6] px-1"
              locales={[esLocale, nlLocale]}
              locale={language.split("_")[0]}
              eventDisplay="true"
              initialView={isMobile ? "dayGridWeek" : "dayGridMonth"}
              displayEventEnd={true}
              events={formatEvents}
              eventContent={renderEventContent}
            />
          </div>
          <div className="md:px-16 md:pt-10 pt-5 md:w-3/4 w-full flex items-center justify-center">
            <div className="w-full">
              <h1 className="text-xl my-5">
                {translation(language).events.futureEvents}
              </h1>
              {formatEvents ? (
                formatEvents?.filter((event: any) =>
                  moment(event.start).isAfter(moment())
                ).length > 0 ? (
                  formatEvents
                    ?.filter((event: any) =>
                      moment(event.start).isAfter(moment())
                    )
                    .slice(0, 5)
                    .map((event: any) => (
                      <div
                        key={event.id}
                        className={classNames(
                          "bg-[#4ab6c6] p-3 mb-2 rounded-lg",
                          "flex justify-between items-center"
                        )}
                      >
                        <div>
                          <p className="text-sm">{event.title}</p>
                          <p className="text-xs">{event.description}</p>
                        </div>
                        <div>
                          <p className="text-xs">
                            {language === "en_US" ? (
                              <>
                                {moment(event.start).format(
                                  "MMMM Do YYYY HH:mm"
                                )}{" "}
                                -{" "}
                                {moment(event.end).format("MMMM Do YYYY HH:mm")}
                              </>
                            ) : (
                              <>
                                {moment(event.start).format(
                                  "DD MMMM YYYY HH:mm"
                                )}{" "}
                                -{" "}
                                {moment(event.end).format("DD MMMM YYYY HH:mm")}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                ) : (
                  <AlertBox
                    type="info"
                    message={translation(language).general.error.noDataFound}
                  />
                )
              ) : (
                <AlertBox
                  type="danger"
                  message={
                    translation(language).general.error.somethingWentWrong
                  }
                />
              )}

              <h1 className="text-xl my-5">
                {translation(language).events.previousEvents}
              </h1>
              {formatEvents ? (
                formatEvents?.filter((event: any) =>
                  moment(event.start).isBefore(moment())
                ).length > 0 ? (
                  formatEvents
                    ?.filter((event: any) =>
                      moment(event.start).isBefore(moment())
                    )
                    .slice(0, 5)
                    .map((event: any) => (
                      <div
                        key={event.id}
                        className={classNames(
                          "bg-secondary p-3 mb-2 rounded-lg",
                          "flex justify-between items-center"
                        )}
                      >
                        <div>
                          <p className="text-sm">{event.title}</p>
                          <p className="text-xs">{event.description}</p>
                        </div>
                        <div>
                          <p className="text-xs">
                            {language === "en_US" ? (
                              <>
                                {moment(event.start).format(
                                  "MMMM Do YYYY HH:mm"
                                )}{" "}
                                -{" "}
                                {moment(event.end).format("MMMM Do YYYY HH:mm")}
                              </>
                            ) : (
                              <>
                                {moment(event.start).format(
                                  "DD MMMM YYYY HH:mm"
                                )}{" "}
                                -{" "}
                                {moment(event.end).format("DD MMMM YYYY HH:mm")}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                ) : (
                  <AlertBox
                    type="info"
                    message={translation(language).general.error.noDataFound}
                  />
                )
              ) : (
                <AlertBox
                  type="danger"
                  message={
                    translation(language).general.error.somethingWentWrong
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default EventsPage;
